import {
  CommandBarItem,
  compactCommands,
  createCommand,
} from '@sketch/modules-common'

import { ReactComponent as MarkAllReadIcon } from '@sketch/icons/double-check-17'
import { ReactComponent as UnreadIcon } from '@sketch/icons/img-mail-envelope-dot-17'
import { ReactComponent as SortIcon } from '@sketch/icons/arrow-up-down-24'
import { ReactComponent as FilterIcon } from '@sketch/icons/3-lines-filter-24'
import { ReactComponent as WorkspaceIcon } from '@sketch/icons/workspace-17'
import { ReactComponent as ResetIcon } from '@sketch/icons/arrow-clockwise-17'

import { InboxSearchOptions, SortDirection } from '../InboxSearchContext'

// The additive filter toggles surfaced as commands — kept in sync with the
// boolean keys in `InboxSearchOptions` and the labels used by `InboxFilters`.
export type InboxFilterKey =
  | 'comments'
  | 'replies'
  | 'mentions'
  | 'downloadableAssets'
  | 'starredUpdates'
  | 'billing'

const FILTER_DEFINITIONS: { key: InboxFilterKey; label: string }[] = [
  { key: 'comments', label: 'Comments' },
  { key: 'replies', label: 'Replies' },
  { key: 'mentions', label: 'Mentions' },
  { key: 'downloadableAssets', label: 'Downloadable Assets' },
  { key: 'starredUpdates', label: 'Starred Updates' },
  { key: 'billing', label: 'Billing' },
]

export interface CreateInboxCommandsProps {
  sort: SortDirection
  options: InboxSearchOptions
  /** True when any filter or the search query is narrowing the list. */
  hasActiveFilters: boolean
  unreadCount: number
  /** Identifier of the newest unread notification, or `null` when there's none. */
  mostRecentUnreadId: string | null
  /** Workspaces the user can filter by (single-select). */
  workspaces: { identifier: string; name: string }[]
  /** Currently selected workspace filter, or `null` for all workspaces. */
  selectedWorkspaceId: string | null
  onMarkAllAsRead: () => void
  onGoToRecentUnread: () => void
  onSetSort: (sort: SortDirection) => void
  onToggleFilter: (key: InboxFilterKey) => void
  onSelectWorkspace: (workspaceId: string | null) => void
  onClearFilters: () => void
}

/**
 * List-level inbox commands (the "Inbox" section). These mirror controls that
 * live in the inbox header — sort and filters — plus "Mark all as read", which
 * the new inbox has no button for yet.
 */
export const createInboxCommands = ({
  sort,
  options,
  hasActiveFilters,
  unreadCount,
  mostRecentUnreadId,
  workspaces,
  selectedWorkspaceId,
  onMarkAllAsRead,
  onGoToRecentUnread,
  onSetSort,
  onToggleFilter,
  onSelectWorkspace,
  onClearFilters,
}: CreateInboxCommandsProps): CommandBarItem[] =>
  compactCommands([
    createCommand(
      {
        id: 'inbox.mark-all-as-read',
        analyticsId: 'inbox.mark-all-as-read',
        title: 'Mark All as Read',
        section: 'Inbox',
        keywords: ['mark', 'all', 'read', 'clear', 'unread', 'inbox'],
        icon: MarkAllReadIcon,
        priority: 20,
        disabledReason:
          unreadCount === 0 ? 'No unread notifications' : undefined,
      },
      onMarkAllAsRead
    ),
    createCommand(
      {
        id: 'inbox.go-to-recent-unread',
        analyticsId: 'inbox.go-to-recent-unread',
        title: 'Go to Most Recent Unread',
        section: 'Inbox',
        keywords: ['unread', 'recent', 'latest', 'newest', 'jump', 'go'],
        icon: UnreadIcon,
        priority: 18,
        disabledReason: mostRecentUnreadId
          ? undefined
          : 'No unread notifications',
      },
      onGoToRecentUnread
    ),
    sort !== 'newest' &&
      createCommand(
        {
          id: 'inbox.sort-newest',
          analyticsId: 'inbox.sort-newest',
          title: 'Sort by Newest First',
          section: 'Inbox',
          keywords: ['sort', 'order', 'newest', 'recent', 'inbox'],
          icon: SortIcon,
          priority: 12,
        },
        () => onSetSort('newest')
      ),
    sort !== 'oldest' &&
      createCommand(
        {
          id: 'inbox.sort-oldest',
          analyticsId: 'inbox.sort-oldest',
          title: 'Sort by Oldest First',
          section: 'Inbox',
          keywords: ['sort', 'order', 'oldest', 'inbox'],
          icon: SortIcon,
          priority: 12,
        },
        () => onSetSort('oldest')
      ),
    // Notification-type filters (multi-select). Each child reflects its on/off
    // state and keeps the bar open so several can be flipped in a row.
    createCommand(
      {
        id: 'inbox.filter-types',
        analyticsId: 'inbox.filter-types',
        title: 'Show only',
        subtitle:
          FILTER_DEFINITIONS.filter(({ key }) => options[key])
            .map(({ label }) => label)
            .join(', ') || 'All types',
        section: 'Inbox',
        // Keep the individual labels searchable so typing e.g. "mentions" still
        // surfaces the "Show only" entry.
        keywords: [
          'filter',
          'show only',
          'type',
          'types',
          ...FILTER_DEFINITIONS.map(({ label }) => label.toLowerCase()),
        ],
        icon: FilterIcon,
        priority: 8,
        children: FILTER_DEFINITIONS.map(({ key, label }) =>
          createCommand(
            {
              id: `inbox.filter-${key}`,
              analyticsId: `inbox.filter-${key}`,
              title: label,
              section: 'Inbox',
              keywords: ['filter', label.toLowerCase()],
              icon: FilterIcon,
              selected: options[key],
              keepOpenOnRun: true,
              priority: 0,
            },
            () => onToggleFilter(key)
          )
        ),
      },
      () => undefined
    ),
    // Workspace filter (single-select), with an "All Workspaces" reset.
    workspaces.length > 0 &&
      createCommand(
        {
          id: 'inbox.filter-workspace',
          analyticsId: 'inbox.filter-workspace',
          title: 'Workspace',
          subtitle:
            workspaces.find(w => w.identifier === selectedWorkspaceId)?.name ??
            'All Workspaces',
          section: 'Inbox',
          keywords: [
            'filter',
            'workspace',
            'from',
            ...workspaces.map(w => w.name.toLowerCase()),
          ],
          icon: WorkspaceIcon,
          priority: 7,
          children: [
            createCommand(
              {
                id: 'inbox.filter-workspace-all',
                analyticsId: 'inbox.filter-workspace-all',
                title: 'All Workspaces',
                section: 'Inbox',
                keywords: ['all', 'workspaces', 'clear', 'reset'],
                icon: WorkspaceIcon,
                selected: selectedWorkspaceId === null,
                keepOpenOnRun: true,
                priority: 1,
              },
              () => onSelectWorkspace(null)
            ),
            ...workspaces.map(workspace =>
              createCommand(
                {
                  id: `inbox.filter-workspace-${workspace.identifier}`,
                  analyticsId: 'inbox.filter-workspace-item',
                  title: workspace.name,
                  section: 'Inbox',
                  keywords: ['workspace', workspace.name.toLowerCase()],
                  icon: WorkspaceIcon,
                  selected: selectedWorkspaceId === workspace.identifier,
                  keepOpenOnRun: true,
                  priority: 0,
                },
                () => onSelectWorkspace(workspace.identifier)
              )
            ),
          ],
        },
        () => undefined
      ),
    hasActiveFilters &&
      createCommand(
        {
          id: 'inbox.clear-filters',
          analyticsId: 'inbox.clear-filters',
          title: 'Reset filters',
          section: 'Inbox',
          keywords: ['clear', 'reset', 'filters', 'search', 'inbox'],
          icon: ResetIcon,
          priority: 10,
        },
        onClearFilters
      ),
  ])
